'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

import allNutrients from '../../../../../tables/nutrients';
import modalStyles from '../../../../../jsx-styles/modals';

import './AddNutrients.scss';

export default class AddNutrients extends Component {
    static propTypes = {
        onAdd: PropTypes.func.isRequired,
        currentNutrients: PropTypes.array,      // Nutrients already added (to exclude)
        availableNutrients: PropTypes.array,     // Only show these nutrients (optional filter)
        buttonText: PropTypes.string,
        buttonClass: PropTypes.string,
        modalTitle: PropTypes.string,
        modalStyle: PropTypes.string,  // 'pro' or 'admin'
    };

    static defaultProps = {
        currentNutrients: [],
        availableNutrients: null,  // null means show all nutrients
        buttonText: 'Add Nutrient',
        buttonClass: null,
        modalStyle: 'pro',
        modalTitle: 'Add Nutrients',
    };

    static contextTypes = {
        showUpgradeForm: PropTypes.func,
        user: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            search: '',
            selectedNutrients: [],
            isModalOpen: false,
        };
    }

    focused = null;

    autofocus = (el) => {
        if (el && !this.focused) {
            this.focused = el;
            el.focus();
            el.select();
        }

        // Component unmounted. Goodbye.
        if (!el) {
            this.focused = null;
        }
    }

    showModal = () => {
        this.setState({isModalOpen: true});
    }

    closeModal = () => {
        this.setState({
            isModalOpen: false,
            selectedNutrients: [],
            search: ''
        });
    }

    toggleNutrient = (nutrNo) => {
        const { selectedNutrients } = this.state;
        const { showUpgradeForm } = this.context;

        if (this.checkLocked(nutrNo)) {
            if (showUpgradeForm) {
                showUpgradeForm({feature: 'add_remove_micros'});
            }

            return;
        }

        const i = selectedNutrients.indexOf(nutrNo);

        if (i === -1) {
            selectedNutrients.push(nutrNo);
        } else {
            selectedNutrients.splice(i, 1);
        }

        this.setState({selectedNutrients});
    }

    matchFilters = (nutrNo) => {
        const { currentNutrients, availableNutrients } = this.props;
        const { search } = this.state;

        // If availableNutrients is specified, nutrient must be in that list
        if (availableNutrients && !availableNutrients.includes(nutrNo)) {
            return false;
        }

        // Nutrient must not already be added
        if (currentNutrients.includes(nutrNo)) {
            return false;
        }

        // Must have valid nutrient info
        if (!(allNutrients[nutrNo] && allNutrients[nutrNo].Filter)) {
            return false;
        }

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            const nutrientName = allNutrients[nutrNo].NutrDesc.toLowerCase();
            const nutrientNo = nutrNo.toLowerCase();
            return nutrientName.includes(searchLower) || nutrientNo.includes(searchLower);
        }

        return true;
    }

    checkLocked = (nutrNo) => {
        const { user } = this.context;
        const { capabilities = {}, role } = user || {};

        if (role === 'admin') {
            return false;
        }

        const macros = ['203', '204', '205', '208'];
        return (macros.includes(nutrNo) && !capabilities.add_remove_micros) || (!macros.includes(nutrNo) && !capabilities.add_remove_micros);
    }

    handleAdd = () => {
        const { onAdd } = this.props;
        const { selectedNutrients } = this.state;
        onAdd(selectedNutrients);
        this.closeModal();
    }

    renderAdminModal = () => {
        const { search, selectedNutrients } = this.state;
        const { modalTitle, availableNutrients } = this.props;

        const alphaSortCmp = (a, b) => allNutrients[a].NutrDesc.localeCompare(allNutrients[b].NutrDesc);
        const nutrients = Object.keys(allNutrients)
                                .filter(nutrNo => this.matchFilters(nutrNo))
                                .sort(alphaSortCmp);

        return (
            <Modal isOpen={true}
                onRequestClose={this.closeModal}
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay"
                contentLabel={modalTitle}
                closeTimeoutMS={250}>

                <div className="el-modal-container el-modal2-container add-nutrients-modal add-nutrients-modal-admin">
                    <header>
                        <h2>{modalTitle}</h2>
                        <button className="el-modal-close-x" onClick={this.closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container">
                        <div className="el-labeled-input el-input-with-icon el-search-input">
                            <i className="icon-search3 left-icon" />
                            <input
                                type="text"
                                placeholder="Search nutrients..."
                                ref={this.autofocus}
                                value={search}
                                onChange={ev => this.setState({search: ev.target.value})}
                            />
                        </div>

                        <div className="nutrients-list-container">
                            {nutrients.length > 0 ? (
                                <ul className="nutrients-list" data-testid="nutrients-list">
                                    {nutrients.map(nutrNo => {
                                        const isLocked = this.checkLocked(nutrNo);
                                        return (
                                            <li key={nutrNo}>
                                                <button
                                                    className="el-checkbox"
                                                    data-active={selectedNutrients.includes(nutrNo)}
                                                    onClick={() => this.toggleNutrient(nutrNo)}
                                                >
                                                    {allNutrients[nutrNo].NutrDesc}
                                                    {isLocked && <i className="icon-lock" />}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="no-results">
                                    <p>
                                        {search
                                            ? 'No matching nutrients found'
                                            : availableNutrients
                                                ? 'No additional nutrients available to add'
                                                : 'No nutrients available'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <footer>
                        <button
                            className="el-modal-cancel-btn"
                            onClick={this.closeModal}
                        >
                            Cancel
                        </button>
                        <button
                            className="el-modal-ok-btn"
                            onClick={this.handleAdd}
                            disabled={selectedNutrients.length === 0}
                        >
                            Add Selected
                        </button>
                    </footer>
                </div>
            </Modal>
        );
    }

    renderProModal = () => {
        const { search, selectedNutrients } = this.state;
        const { modalTitle } = this.props;

        const alphaSortCmp = (a, b) => allNutrients[a].NutrDesc.localeCompare(allNutrients[b].NutrDesc);
        const nutrients = Object.keys(allNutrients)
                                .filter(nutrNo => this.matchFilters(nutrNo))
                                .sort(alphaSortCmp);

        return (
            <Modal isOpen={true}
                onRequestClose={this.closeModal}
                closeModal={this.closeModal}
                className="add-remove-nutrients-modal"
                contentLabel={modalTitle}
                style={modalStyles.largeSquareModal}
                closeTimeoutMS={250}>

                <div className="add-remove-nutrients-container editor-modal-container">
                    <header className="modal-header">
                        <h2>{modalTitle}</h2>
                        <button className="close-btn" onClick={this.closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="search">
                        <input type="text" placeholder="Search"
                            ref={this.autofocus}
                            value={search}
                            onChange={ev => this.setState({search: ev.target.value})} />
                    </div>

                    <div className="editor-scrollable">
                        <ul data-testid="nutrients-list">
                            {nutrients.map(nutrNo => {
                                return (
                                    <li key={nutrNo}>
                                        <label className="global-search-checkbox">
                                            <input type="checkbox" checked={selectedNutrients.includes(nutrNo)}
                                                onChange={e => this.toggleNutrient(nutrNo)} />
                                            <span>
                                                {allNutrients[nutrNo].NutrDesc}
                                                {this.checkLocked(nutrNo) ? <i className="icon-lock" /> : null}
                                            </span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    <footer>
                        <button className="cancel" onClick={this.closeModal}>Cancel</button>
                        <button className="accept" onClick={this.handleAdd}>Add</button>
                    </footer>
                </div>
            </Modal>
        );
    }

    renderAddNutrientsModal = () => {
        const { isModalOpen } = this.state;

        if (!isModalOpen) {
            return null;
        }

        return this.props.modalStyle == 'pro' ? this.renderProModal() : this.renderAdminModal();
    }

    render() {
        const { buttonText } = this.props;
        let { buttonClass } = this.props;

        if (!buttonClass) {
            buttonClass = this.props.modalStyle == 'pro'
                ? 'nutrient-btn'
                : 'el-medium-btn el-raspberry-outline-btn el-btn-icon-left';
        }

        return (
            <span className="add-remove-nutrients">
                <button className={buttonClass} onClick={this.showModal}>
                    <i className="icon-plus-thin" />
                    {buttonText}
                </button>

                {this.renderAddNutrientsModal()}
            </span>
        );
    }
}
